import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FR0600_BOXES, type Fr0600Box } from '@fsp/core';
import { api, ApiError, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface TaxSettings { vatPayer: boolean; vatPeriod: 'month' | 'half_year'; employees: boolean; dividends: boolean; euSupplies: boolean; psdMembers: number; sodraCode: string | null; vatDeductionPercent?: number; advanceProfitTax?: boolean; advanceProfitMethod?: 'history' | 'forecast' }
export interface TaxItem {
  obligation: string; period: string; due: string; title: string; text: string; url: string; daysLeft: number;
  done: { at: string; by: string | null } | null; vat?: { issued: number; received: number; toPay: number }; amount?: number;
  payment?: { kind: 'sodra' | 'vmi' | 'psd'; expected: number | null; paid: number; payments: { id: number; date: string; amount: number; period: string; auto: boolean }[] };
  declared?: boolean; documents?: { id: number; fileName: string; createdAt: string }[];
}

/** Uploads a filed declaration (SAM, GPM313, FR0600…) for an obligation and period. */
async function uploadDeclaration(obligation: string, period: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('obligation', obligation);
  body.append('period', period);
  body.append('file', file, file.name);
  const response = await fetch('api/v1/taxes/documents', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
  if (!response.ok) {
    const code = ((await response.json().catch(() => null)) as { error?: { code?: string } } | null)?.error?.code;
    throw new ApiError(response.status, code ?? 'upload_failed');
  }
}

/** Paid from the bank for the obligation's period, against what is owed (VMI: GPM and VAT of the period together). */
function TaxPaid({ item, readOnly, onMove }: { item: TaxItem; readOnly: boolean; onMove: (id: number, period: string) => void }) {
  const { t } = useI18n();
  const p = item.payment;
  if (!p) return null;
  const diff = p.expected === null ? null : p.paid - p.expected;
  const state = !p.paid ? (item.daysLeft < 0 ? <span className="tax-flag is-late">{t('nesumokėta')}</span> : null)
    : diff === null ? null : diff === 0 ? <span className="tax-flag is-ok">✓ {t('sumokėta')}</span>
    : diff < 0 ? <span className="tax-flag is-late">{t('trūksta {sum}', { sum: money(-diff) })}</span> : <span className="tax-flag">{t('permokėta {sum}', { sum: money(diff) })}</span>;
  return (
    <div className="tax-paid">
      <p>{p.kind === 'vmi' ? t('Sumokėta VMI už šį laikotarpį (GPM ir PVM kartu): {sum}', { sum: money(p.paid) }) : t('Sumokėta Sodrai: {sum}', { sum: money(p.paid) })}
        {p.expected !== null ? ` · ${t('priskaičiuota {sum}', { sum: money(p.expected) })}` : ''} {state}</p>
      {p.payments.length ? <ul>{p.payments.map((x) => (
        <li key={x.id}>{x.date} · {money(x.amount)}{!x.auto ? <small> · {t('priskirta ranka')}</small> : null}
          {!readOnly ? <button type="button" className="text-link" onClick={() => onMove(x.id, x.auto ? '' : x.period)}>{t('Kitas laikotarpis')}</button> : null}</li>
      ))}</ul> : null}
    </div>
  );
}
type TaxAction = { kind: 'period'; id: number; period: string } | { kind: 'delete'; id: number; fileName: string };

function TaxActionDialog({ action, busy, error, onClose, onConfirm }: {
  action: TaxAction | null; busy: boolean; error: unknown; onClose: () => void; onConfirm: (period: string | null) => void;
}) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (action) dialog.current?.showModal(); else dialog.current?.close(); }, [action]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const period = new FormData(event.currentTarget).get('period');
    if (!busy) onConfirm(typeof period === 'string' ? period.trim() || null : null);
  };
  return <dialog ref={dialog} className="confirm-dialog" onClose={onClose} onCancel={(event) => { if (busy) event.preventDefault(); }}>
    {action ? <form className="dialog-card" key={`${action.kind}-${action.id}`} onSubmit={submit}>
      <h2>{action.kind === 'period' ? t('Kitas laikotarpis') : t('Pašalinti {name}?', { name: action.fileName })}</h2>
      {action.kind === 'period' ? <label>{t('Už kokį laikotarpį šis mokėjimas? (pvz. 2026-09, 2026-H1 arba 2026; tuščia — pagal mokėjimo datą)')}
        <input name="period" defaultValue={action.period} pattern="[0-9]{4}(-[0-9]{2}|-H[12])?" autoFocus disabled={busy} />
      </label> : null}
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <div className="dialog-actions">
        <button type="button" className="button button--ghost" disabled={busy} onClick={onClose}>{t('Atšaukti')}</button>
        <button type="submit" className="button button--primary" disabled={busy}>{action.kind === 'period' ? t('Išsaugoti') : t('Pašalinti')}</button>
      </div>
    </form> : null}
  </dialog>;
}

interface Taxes { settings: TaxSettings; today: string; items: TaxItem[] }

export const useTaxes = (enabled = true) => useQuery({ queryKey: ['taxes'], enabled, queryFn: () => api.get<Taxes>('api/v1/taxes') });

/** "2026-09" → "2026 rugsėjis"; "2026-H1" → "2026 I pusmetis"; "2026" → "2026 m." */
function periodText(period: string, locale: string, t: (s: string) => string): string {
  if (/^\d{4}-\d{2}$/.test(period)) return new Date(`${period}-01T00:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  if (period.includes('-H')) return `${period.slice(0, 4)} ${period.endsWith('1') ? t('I pusmetis') : t('II pusmetis')}`;
  return `${period} ${t('m.')}`;
}

function DueBadge({ item }: { item: TaxItem }) {
  const { t } = useI18n();
  if (item.done) return <span className="badge badge--paid">✓ {t('Atlikta')}</span>;
  if (item.daysLeft < 0) return <span className="badge badge--overdue">{t('Praleista {n} d.', { n: -item.daysLeft })}</span>;
  if (item.daysLeft === 0) return <span className="badge badge--overdue">{t('Šiandien')}</span>;
  return <span className={`badge ${item.daysLeft <= 5 ? 'badge--draft' : 'badge--sent'}`}>{t('Liko {n} d.', { n: item.daysLeft })}</span>;
}

/** Mokesčių kalendorius: deadlines that apply to the company, VAT to pay, and marking them done. */
export function TaxesPage({ me }: { me: Me }) {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const data = useTaxes();
  const [action, setAction] = useState<TaxAction | null>(null);
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['taxes'] }) });
  const openAction = (next: TaxAction) => { act.reset(); setAction(next); };
  const confirmAction = async (period: string | null) => {
    if (!action) return;
    try {
      await act.mutateAsync(() => action.kind === 'period' ? api.patch(`api/v1/taxes/payments/${action.id}`, { period }) : api.delete(`api/v1/taxes/documents/${action.id}`));
      setAction(null);
    } catch { /* Keep the action and its error visible for retry. */ }
  };
  const s = data.data?.settings;
  const save = (patch: Partial<TaxSettings>) => s && act.mutate(() => api.put('api/v1/taxes/settings', { vatPeriod: s.vatPeriod, employees: s.employees, dividends: s.dividends, euSupplies: s.euSupplies, psdMembers: s.psdMembers, ...patch }));
  const readOnly = me.company.role === 'viewer' || Boolean(me.viewAsAdmin);
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Mokesčiai')} title={t('Mokesčių kalendorius')} text={t('Artimiausi deklaravimo ir mokėjimo terminai. Savaitgaliais ir švenčių dienomis terminas perkeliamas į kitą darbo dieną.')} />}>
      {data.isError || act.isError ? <Alert kind="error">{errorText(t, data.error ?? act.error)}</Alert> : null}
      <TaxActionDialog action={action} busy={act.isPending} error={act.error} onClose={() => setAction(null)} onConfirm={confirmAction} />
      <div className="taxes-layout">
        <section className="panel-card">
          <div className="card-head"><div><p className="eyebrow">{t('Terminai')}</p><h2>{t('Ką ir iki kada')}</h2></div></div>
          {data.isSuccess && !data.data.items.length ? <p className="muted">{t('Pagal nustatymus artimiausių terminų nėra.')}</p> : null}
          <ul className="tax-list">
            {data.data?.items.map((i) => (
              <li key={`${i.obligation}-${i.period}`} className={i.done ? 'is-done' : i.daysLeft < 0 ? 'is-late' : undefined}>
                <div className="tax-date"><strong>{new Date(`${i.due}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long' })}</strong><DueBadge item={i} /></div>
                <div className="tax-body">
                  <strong>{t(i.title)}</strong> <span className="muted">· {periodText(i.period, locale, t)}</span>
                  <p>{t(i.text)}</p>
                  {i.vat ? <p className="tax-vat">{t('PVM pagal sąskaitas: išrašytų {a} − gautų {b} = {c}', { a: money(i.vat.issued), b: money(i.vat.received), c: money(i.vat.toPay) })}
                    {i.vat.toPay < 0 ? ` (${t('grąžintina')})` : ''}</p> : null}
                  {i.amount !== undefined ? <p className="tax-vat">{t('Pagal patvirtintus atlyginimus: {sum}', { sum: money(i.amount) })}</p> : null}
                  <TaxPaid item={i} readOnly={readOnly} onMove={(id, period) => openAction({ kind: 'period', id, period })} />
                  {i.declared ? <div className="tax-docs">
                    {(i.documents ?? []).map((d) => (
                      <span key={d.id} className="tax-doc"><a href={`api/v1/taxes/documents/${d.id}/file`} target="_blank" rel="noreferrer">📎 {d.fileName}</a>
                        {!readOnly ? <button type="button" className="delete-x" aria-label={t('Pašalinti')} onClick={() => openAction({ kind: 'delete', id: d.id, fileName: d.fileName })}>×</button> : null}</span>
                    ))}
                    {!readOnly ? <label className="text-link file-link">{(i.documents ?? []).length ? t('+ dar vienas dokumentas') : t('Įkelti pateiktą deklaraciją')}
                      <input type="file" hidden accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) act.mutate(() => uploadDeclaration(i.obligation, i.period, f)); }} /></label> : null}
                  </div> : null}
                  <small><a href={i.url} target="_blank" rel="noreferrer">{t('Daugiau')} ↗</a>{i.done ? ` · ${t('pažymėjo {name}', { name: i.done.by ?? '—' })}` : ''}</small>
                </div>
                {!readOnly ? <button type="button" className={`button button--small ${i.done ? 'button--ghost' : 'button--primary'}`} disabled={act.isPending}
                  onClick={() => act.mutate(() => api.post('api/v1/taxes/done', { obligation: i.obligation, period: i.period, done: !i.done }))}>{i.done ? t('Atšaukti') : t('Atlikta')}</button> : null}
              </li>
            ))}
          </ul>
          <p className="muted tax-note">{t('Terminai yra bendri mažai įmonei. Ar jie jums taikomi, pasitikslinkite su buhalteriu.')}</p>
        </section>
        <section className="panel-card">
          <p className="eyebrow">{t('Nustatymai')}</p><h2>{t('Kas taikoma jūsų įmonei')}</h2>
          {s ? (
            <fieldset className="plain-fieldset tax-settings" disabled={readOnly || act.isPending}>
              <p className="muted">{s.vatPayer ? t('Įmonė yra PVM mokėtoja.') : t('Įmonė nėra PVM mokėtoja.')} <Link to="/company" className="text-link">{t('Keisti')}</Link></p>
              {s.vatPayer ? <label>{t('PVM mokestinis laikotarpis')}<select value={s.vatPeriod} onChange={(e) => save({ vatPeriod: e.target.value as TaxSettings['vatPeriod'] })}>
                <option value="month">{t('Kalendorinis mėnuo')}</option><option value="half_year">{t('Pusmetis')}</option></select></label> : null}
              <label className="recurring-check"><input type="checkbox" checked={s.employees} onChange={(e) => save({ employees: e.target.checked })} />{t('Turime darbuotojų (mokame atlyginimus)')}</label>
              <label className="recurring-check"><input type="checkbox" checked={s.dividends} onChange={(e) => save({ dividends: e.target.checked })} />{t('Mokame dividendus')}</label>
              {s.employees ? <label>{t('Sodros draudėjo kodas')}
                <input inputMode="numeric" maxLength={7} pattern="[0-9]{7}" defaultValue={s.sodraCode ?? ''} key={s.sodraCode ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (s.sodraCode ?? '') && (v === '' || /^\d{7}$/.test(v))) save({ sodraCode: v }); }} />
                <small className="muted">{t('7 skaitmenų kodas iš Sodros (ne įmonės kodas): reikalingas SAM pranešimo failui.')}</small></label> : null}
              <label>{t('MB nariai, už kuriuos įmonė moka PSD (nedirba ir kitur nedraudžiami)')}
                <input type="number" min={0} max={50} defaultValue={s.psdMembers} key={s.psdMembers} onBlur={(e) => { const n = Math.max(0, Math.min(50, Number(e.target.value) || 0)); if (n !== s.psdMembers) save({ psdMembers: n }); }} />
                <small className="muted">{t('Kiekvieno nario PSD — 6,98 % MMA per mėnesį (2026 m. — 80,48 €), iki mėnesio pabaigos, kodas 322. Deklaracijos nereikia.')}</small></label>
              {s.vatPayer ? <label className="recurring-check"><input type="checkbox" checked={s.euSupplies} onChange={(e) => save({ euSupplies: e.target.checked })} />{t('Parduodame PVM mokėtojams kitose ES šalyse')}</label> : null}
              <label className="recurring-check"><input type="checkbox" checked={Boolean(s.advanceProfitTax)} onChange={(e) => save({ advanceProfitTax: e.target.checked })} />{t('Praėjusių metų apmokestinamosios pajamos viršijo 300 000 € (avansinis pelno mokestis, FR0430)')}</label>
              {s.advanceProfitTax ? (
                <label>{t('Avansinis pelno mokestis skaičiuojamas')}
                  <select value={s.advanceProfitMethod ?? 'history'} onChange={(e) => save({ advanceProfitMethod: e.target.value as 'history' | 'forecast' })}>
                    <option value="history">{t('pagal praėjusių metų rezultatus (FR0430 iki kovo 15 ir rugsėjo 15 d.)')}</option>
                    <option value="forecast">{t('pagal prognozę (FR0430 vieną kartą, iki kovo 15 d.)')}</option>
                  </select>
                  <small className="muted">{t('Pirmąjį mokestinį laikotarpį naujai įregistruota įmonė avansinio pelno mokesčio nemoka.')}</small>
                </label>
              ) : null}
            </fieldset>
          ) : null}
        </section>
      </div>
      {s?.vatPayer ? <VatReturn halfYear={s.vatPeriod === 'half_year'} deduction={s.vatDeductionPercent ?? 100} onDeduction={(v) => save({ vatDeductionPercent: v })} /> : null}
    </PanelShell>
  );
}

/** The VAT return FR0600 worked out from the invoices: the boxes, codes it could not place, and the file for EDS. */
export function VatReturn({ halfYear, deduction, onDeduction }: { halfYear: boolean; deduction: number; onDeduction: (v: number) => void }) {
  const { t } = useI18n();
  const previous = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toLocaleDateString('sv-SE').slice(0, 7); };
  const [month, setMonth] = useState(previous);
  const [error, setError] = useState<unknown>(null);
  const [adjust, setAdjust] = useState<{ box: string; amount: string }[]>([]);
  const [byHand, setByHand] = useState(false);
  const year = Number(month.slice(0, 4)), m = Number(month.slice(5, 7));
  const from = halfYear ? `${year}-${m <= 6 ? '01' : '07'}-01` : `${month}-01`;
  const to = halfYear ? `${year}-${m <= 6 ? '06-30' : '12-31'}` : new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10);
  const body = { from, to, adjust: Object.fromEntries(adjust.filter((a) => a.box && Number(a.amount)).map((a) => [a.box, Math.round(Number(a.amount.replace(',', '.')))])), ignoreUnmapped: byHand };
  const data = useQuery({ queryKey: ['fr0600', body], queryFn: () => api.post<{ boxes: Partial<Record<Fr0600Box, number>>; unmapped: { direction: string; code: string; net: number }[]; invoices: number }>('api/v1/taxes/fr0600/preview', body) });
  const r = data.data;
  const download = async () => {
    setError(null);
    try {
      const res = await fetch('api/v1/taxes/fr0600.ffdata', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-fsp-request': '1' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json().catch(() => null) as { error?: { code?: string } } | null; throw new ApiError(res.status, e?.error?.code ?? 'server_error'); }
      const href = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = href; a.download = `FR0600_${from.slice(0, 7)}.ffdata`; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) { setError(e); }
  };
  const editable = FR0600_BOXES.filter((b) => !['28', '35', '36'].includes(b.box));
  return (
    <section className="panel-card vat-return">
      <div className="card-head">
        <div><p className="eyebrow">FR0600 · {from} – {to}</p><h2>{t('PVM deklaracija')}</h2></div>
        <div className="form-actions">
          <input type="month" aria-label={t('Mėnuo')} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          <button type="button" className="button button--primary button--small" disabled={!r || (r.unmapped.length > 0 && !byHand)} onClick={() => void download()}>{t('FR0600 failas (EDS)')}</button>
        </div>
      </div>
      <p className="muted">{t('Apskaičiuota iš šio laikotarpio sąskaitų ir Z ataskaitų pagal PVM klasifikatoriaus kodus. Sumos – sveikais eurais. Prieš pateikdamas buhalteris patikrina; failas įkeliamas EDS per „Persiųsti užpildytą formą“.')}</p>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      {r?.unmapped.length ? <>
        <Alert kind="error">{t('Šių PVM kodų programa nepriskiria FR0600 laukeliams – deklaraciją užpildykite rankiniu būdu arba pakeiskite kodą: {list}', { list: r.unmapped.map((u) => `${u.code} (${u.direction === 'outgoing' ? t('pardavimai') : t('pirkimai')}, ${money(u.net)})`).join(', ') })}</Alert>
        <label className="recurring-check"><input type="checkbox" checked={byHand} onChange={(e) => setByHand(e.target.checked)} />{t('Šias sumas įrašiau rankiniu būdu žemiau – leisti failą')}</label>
      </> : null}
      <div className="vat-return-grid">
        <div>
          {r ? (Object.keys(r.boxes).length ? (
            <table className="journal-lines">
              <tbody>
                {FR0600_BOXES.filter((b) => r.boxes[b.box] !== undefined).map((b) => (
                  <tr key={b.box} className={b.box === '36' ? 'vat-return-total' : ''}><td className="account-code">{b.box}</td><td>{t(b.label)}</td><td className="right">{b.box === '28' ? `${r.boxes[b.box]} %` : `${r.boxes[b.box]} €`}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <p className="empty">{t('Šiuo laikotarpiu deklaruotinų sandorių nėra.')}</p>) : null}
        </div>
        <div className="vat-return-side">
          <label>{t('Proporcinis PVM atskaitos procentas (28)')}
            <input type="number" min={0} max={100} defaultValue={deduction} key={deduction} onBlur={(e) => { const v = Math.max(0, Math.min(100, Math.ceil(Number(e.target.value) || 0))); if (v !== deduction) onDeduction(v); }} /></label>
          <p className="eyebrow">{t('Rankiniai papildymai (eurais)')}</p>
          <p className="muted">{t('Marža (16), importo PVM (26, 27) ir pataisymai, kurių sąskaitos neparodo.')}</p>
          {adjust.map((a, i) => (
            <div key={i} className="pln204-nd">
              <select value={a.box} onChange={(e) => setAdjust(adjust.map((x, k) => (k === i ? { ...x, box: e.target.value } : x)))}>
                <option value="">—</option>{editable.map((b) => <option key={b.box} value={b.box}>{b.box} {t(b.label)}</option>)}</select>
              <input inputMode="numeric" value={a.amount} onChange={(e) => setAdjust(adjust.map((x, k) => (k === i ? { ...x, amount: e.target.value } : x)))} aria-label={t('Suma')} />
              <button type="button" className="icon-button" aria-label={t('Pašalinti')} onClick={() => setAdjust(adjust.filter((_, k) => k !== i))}>×</button>
            </div>
          ))}
          <button type="button" className="text-link" onClick={() => setAdjust([...adjust, { box: '', amount: '' }])}>+ {t('Eilutė')}</button>
        </div>
      </div>
    </section>
  );
}

/** Apžvalga: the next deadlines that are not done. */
export function UpcomingTaxes() {
  const { t, locale } = useI18n();
  const data = useTaxes();
  const [action, setAction] = useState<TaxAction | null>(null);
  const next = (data.data?.items ?? []).filter((i) => !i.done && i.daysLeft >= -30).slice(0, 4);
  if (!data.isSuccess || !next.length) return null;
  return (
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Mokesčiai')}</p><h2>{t('Artimiausi terminai')}</h2></div><Link to="/taxes">{t('Mokesčių kalendorius')} →</Link></div>
      <ul className="tax-list tax-list--compact">
        {next.map((i) => (
          <li key={`${i.obligation}-${i.period}`} className={i.daysLeft < 0 ? 'is-late' : undefined}>
            <div className="tax-date"><strong>{new Date(`${i.due}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long' })}</strong><DueBadge item={i} /></div>
            <div className="tax-body"><strong>{t(i.title)}</strong> <span className="muted">· {periodText(i.period, locale, t)}</span>
              {i.vat ? <small>{t('PVM mokėti: {sum}', { sum: money(Math.max(0, i.vat.toPay)) })}</small> : null}
              {i.amount !== undefined ? <small>{t('Mokėti: {sum}', { sum: money(i.amount) })}</small> : null}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}
